import React, { useState } from 'react'
import { Box, Collapse, IconButton, Table, TableBody, TableCell, TableHead, TableRow, Typography, useTheme } from '@mui/material'
import { format } from 'date-fns'
import { formatTimeAsLocal } from '../../utils/functions/timeUtils'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import { de } from 'date-fns/locale/de'
import { enUS } from 'date-fns/locale/en-US'
import { useSelector } from 'react-redux'
import { IUser } from '../../interface/IUser'
import { useTranslation } from 'react-i18next'
import ReportDetailsLine from './ReportDetailsLine'

const ReportLine = (props: any) => {
    const { row, expandAll, hasTimeCard, onCallUser } = props as { row: any; id: number; expandAll: boolean; hasTimeCard: boolean; onCallUser: boolean }
    const theme = useTheme()
    const user: IUser = useSelector((state: any) => state.user?.value)
    const { t, i18n } = useTranslation()

    const [open, setOpen] = useState(false)

    const startTime = row?.calendarLines.filter((e: any) => e.mserp_timeandattendancejobid === `${process.env.REACT_APP_JID_START}` && e.mserp_oncalltimeandattendancejobid !== 'RB' && e.mserp_oncalltimeandattendancejobid !== 'SE')[0]
        ?.mserp_startdatetime
    const stopLine = row?.calendarLines.find((e: any) => e.mserp_timeandattendancejobid === `${process.env.REACT_APP_JID_STOP}` && e.mserp_oncalltimeandattendancejobid !== 'RB' && e.mserp_oncalltimeandattendancejobid !== 'SE')
    const stopTime = stopLine ? stopLine.mserp_stopdatetime : undefined
    const breakTime =
        Number((row?.calendarLines.filter((e: any) => e.mserp_timeandattendancejobid === `${process.env.REACT_APP_JID_BREAK}`).reduce((sum: number, curr: any) => sum + (curr.mserp_calculatedtimeseconds || 0), 0) / 60.0 / 60.0).toFixed(2)) || 0

    const filteredLines: any[] = row?.calendarLines.filter(
        (line: any) =>
            line.mserp_timeandattendancejobid !== `${process.env.REACT_APP_JID_START}` && line.mserp_timeandattendancejobid !== `${process.env.REACT_APP_JID_STOP}` && line.mserp_timeandattendancejobid !== `${process.env.REACT_APP_JID_BREAK}`
    )

    const onCall: boolean = row?.calendarLines.filter((e: any) => e.mserp_oncalltimeandattendancejobid !== undefined && e.mserp_oncalltimeandattendancejobid !== null && e.mserp_oncalltimeandattendancejobid !== '').length > 0

    const onSE: boolean = row?.calendarLines.filter((e: any) => e.mserp_oncalltimeandattendancejobid === 'SE').length > 0

    let status: string = 'calendar.open'
    let holidayStatus: string = ''
    if (row?.isReleased) status = 'calendar.released'
    if (row?.isHoliday) holidayStatus = 'calendar.holiday'
    if (row?.isAbsent) {
        status = 'calendar.absence'
        holidayStatus = ''
    }
    if (row?.isSaved) {
        status = 'calendar.saved'
    }

    let travelTime: number = 0
    const mbQuantityTravel = row?.header?.mserp_mbquantitytravel
    if (mbQuantityTravel) {
        travelTime = isNaN(mbQuantityTravel) ? 0 : mbQuantityTravel
    }

    let workTimeAccordingArbZG: number = 0.0
    if (startTime !== undefined && stopTime !== undefined) {
        workTimeAccordingArbZG = Number(((new Date(stopTime).getTime() - new Date(startTime).getTime()) / 1000 / 60 / 60 - breakTime - travelTime).toFixed(2))
    }

    return (
        <>
            <TableRow sx={{ '& > *': { borderBottom: 'unset' } }}>
                <TableCell sx={{ padding: 1 }}>
                    <IconButton aria-label="expand row" size="small" onClick={() => setOpen(!open)}>
                        {open || expandAll ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                    </IconButton>
                </TableCell>
                <TableCell sx={{ padding: 1 }} component="th" scope="row">
                    <b>{i18n.language === 'de' ? format(row?.date, 'dd.MM.yyyy') : format(row?.date, 'MM/dd/yyyy')}</b>
                    <br />
                    {format(row?.date, 'EEEE', { locale: i18n.language === 'de' ? de : enUS })}
                </TableCell>
                {hasTimeCard && (
                    <>
                        <TableCell align="right" sx={{ padding: 1 }}>
                            {formatTimeAsLocal(startTime, row?.date)}
                        </TableCell>
                        <TableCell align="right" sx={{ padding: 1 }}>
                            {formatTimeAsLocal(stopTime, row?.date)}
                        </TableCell>
                        <TableCell align="right" sx={{ padding: 1 }}>
                            {Number(breakTime) > 0 ? breakTime : ''}
                        </TableCell>
                        <TableCell align="right" sx={{ padding: 1 }}>
                            {Number(row?.calendarHeader?.mserp_mbquantitytravel) > 0 ? row?.calendarHeader?.mserp_mbquantitytravel : ''}
                        </TableCell>
                        <TableCell align="right" sx={{ padding: 1 }}>
                            {workTimeAccordingArbZG}
                        </TableCell>
                        <TableCell align="right" sx={{ padding: 1 }}>
                            {Number(row?.dayInformation?.mserp_timeprofileseconds) > 0 ? row?.dayInformation?.mserp_timeprofileseconds : ''}
                        </TableCell>
                    </>
                )}

                <>
                    <TableCell align="right" sx={{ padding: 1 }}>
                        <Box sx={{ display: 'flex', gap: 2, alignContent: 'end' }}>
                            <Typography variant="body2">{row?.dayInformation?.mserp_totalpayflextimebalanceseconds.toFixed(2)}</Typography>
                            {row?.dayInformation?.mserp_totalpayflextimeaddseconds > 0 ? (
                                <Typography variant="body2" color="#008DFC">
                                    {' '}
                                    (+{row?.dayInformation?.mserp_totalpayflextimeaddseconds.toFixed(2)})
                                </Typography>
                            ) : (
                                <></>
                            )}
                            {!hasTimeCard ? <Typography variant="body2"> {filteredLines?.reduce((sum, curr) => sum + (curr.mserp_hours || 0), 0)}</Typography> : <></>}
                            {row?.dayInformation?.mserp_totalpayflextimesubseconds > 0 ? (
                                <Typography variant="body2" color="#CC215A">
                                    {' '}
                                    (-{row?.dayInformation?.mserp_totalpayflextimesubseconds.toFixed(2)})
                                </Typography>
                            ) : (
                                <></>
                            )}
                        </Box>
                    </TableCell>
                    {onCallUser && <TableCell sx={{ padding: 1 }}>{onCall ? t('report.yes') : t('report.no')}</TableCell>}
                </>
                <TableCell sx={{ padding: 1 }}>
                    {t(status)} {holidayStatus ? `- ${t(holidayStatus)}` : ''}
                </TableCell>
            </TableRow>
            <TableRow style={{ backgroundColor: theme.palette.background.default }}>
                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={hasTimeCard ? onCallUser ? 11 : 10 : 4}>
                    <Collapse in={open || expandAll} timeout="auto" unmountOnExit>
                        <Box sx={{ margin: 1, marginLeft: 8 }}>
                            {filteredLines?.length > 0 ? (
                                <Table size="small" aria-label="purchases">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ padding: 0 }}>{t('report.project')}</TableCell>
                                            <TableCell sx={{ padding: 0 }}>{t('report.activity')}</TableCell>
                                            <TableCell sx={{ padding: 0 }} align="right">
                                                {t('report.amount')}
                                            </TableCell>
                                            {user?.worker?.mserp_employmentcategory === 'ANÜ' || !hasTimeCard ? (
                                                <TableCell sx={{ padding: 0 }} align="right">
                                                    {t('report.order')}
                                                </TableCell>
                                            ) : (
                                                <></>
                                            )}
                                            {onSE ? (
                                                <>
                                                    <TableCell sx={{ paddingLeft: 5, paddingY: 0 }} align="right">
                                                        {t('report.from')}
                                                    </TableCell>
                                                    <TableCell sx={{ paddingLeft: 5, paddingY: 0 }} align="right">
                                                        {t('report.to')}
                                                    </TableCell>
                                                </>
                                            ) : (
                                                <></>
                                            )}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {filteredLines?.map((historyRow: any) => {
                                            return <ReportDetailsLine key={filteredLines.indexOf(historyRow)} row={historyRow} hasTimeCard={hasTimeCard} />
                                        })}
                                    </TableBody>
                                </Table>
                            ) : (
                                <>{t('report.nodata')}</>
                            )}
                        </Box>
                    </Collapse>
                </TableCell>
            </TableRow>
        </>
    )
}

export default ReportLine
